import React, { createContext, useContext, useEffect } from "react";

type ThemeMode = "light" | "dark" | "system";

interface ThemeContextType {
  themeMode: ThemeMode;
  actualTheme: "light" | "dark";
  setThemeMode: (mode: ThemeMode) => void;
}

/**
 * Minimal ThemeContext:
 * - Forces dark mode on the document element
 * - Exposes a safe no-op setThemeMode so existing code doesn't break
 */
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    if (typeof document !== "undefined") {
      // Force dark class once on mount
      document.documentElement.classList.remove("light");
      document.documentElement.classList.add("dark");
    }
  }, []);

  const value: ThemeContextType = {
    themeMode: "dark",
    actualTheme: "dark",
    setThemeMode: (_mode: ThemeMode) => {
      // no-op intentionally to keep API stable
      // you can log or persist if you want in future
      return;
    },
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    // If someone forgot to wrap with provider, still return safe defaults
    return {
      themeMode: "dark",
      actualTheme: "dark",
      setThemeMode: () => {},
    };
  }
  return context;
};
